import type { AdminRouterContext } from '@/lib/router-context'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import SaveBar from '@/components/forms/SaveBar'
import { useIsReadOnly, type AppAbility } from '@/lib/casl'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import {
  googleAnalyticsQueries,
  useRemoveGoogleAnalyticsIntegrationMutation,
  useSuspenseGoogleAnalyticsIntegration,
  useUpdateGoogleAnalyticsIntegrationMutation,
} from '@/lib/services/google-analytics/google-analytics.query'
import {
  googleAnalyticsIntegrationSchema,
  normalizeGoogleAnalyticsMeasurementId,
} from '@/lib/services/google-analytics/google-analytics.schema'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { BarChart3, ExternalLink, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

function canViewGoogleAnalytics(ability: Pick<AppAbility, 'can'>) {
  return (
    ability.can('list', 'business-settings') &&
    ability.can('list', 'google-analytics') &&
    ability.can('view', 'stores')
  )
}

function canEditGoogleAnalytics(ability: Pick<AppAbility, 'can'>) {
  return ability.can('edit', 'google-analytics') && ability.can('edit', 'stores')
}

export async function googleAnalyticsSettingsLoader({
  context,
  params,
}: {
  context: {
    ability: Pick<AdminRouterContext['ability'], 'can'>
    queryClient: AdminRouterContext['queryClient']
  }
  params: { orgId: string | number }
}): Promise<{ isReadOnly: boolean }> {
  if (!canViewGoogleAnalytics(context.ability)) throw notFound()

  const isReadOnly = !canEditGoogleAnalytics(context.ability)
  const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

  if (isGlobalOrg) return { isReadOnly }

  await context.queryClient.ensureQueryData(
    googleAnalyticsQueries.integration({ organizationId: params.orgId })
  )

  return { isReadOnly }
}

export const Route = createFileRoute(
  '/_authenticated/$orgId/settings/integrations/google-analytics/'
)({
  component: GoogleAnalyticsSettingsPage,
  loader: googleAnalyticsSettingsLoader,
})

function OrganizationGoogleAnalyticsSettings() {
  const orgId = useRouteOrganizationIdParam()
  const { isReadOnly } = useIsReadOnly()
  const { data: integration } = useSuspenseGoogleAnalyticsIntegration({ organizationId: orgId })
  const updateMutation = useUpdateGoogleAnalyticsIntegrationMutation()
  const removeMutation = useRemoveGoogleAnalyticsIntegrationMutation()
  const form = useAppForm({
    defaultValues: {
      measurement_id: integration.measurement_id ?? '',
    },
    validators: {
      onSubmit: googleAnalyticsIntegrationSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return undefined

        try {
          const savedIntegration = await updateMutation.mutateAsync({
            measurement_id: normalizeGoogleAnalyticsMeasurementId(value.measurement_id),
          })
          const savedValues = { measurement_id: savedIntegration.measurement_id ?? '' }

          toast.success('Google Analytics connected')
          setTimeout(() => formApi.reset(savedValues))
          return undefined
        } catch (error) {
          toast.error('Could not save Google Analytics', {
            description: getErrorMessage(error),
          })

          return serverFieldErrors(error)
        }
      },
    },
  })

  async function removeIntegration() {
    try {
      await removeMutation.mutateAsync()
      form.reset({ measurement_id: '' })
      toast.success('Google Analytics disconnected')
    } catch (error) {
      toast.error('Could not disconnect Google Analytics', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <AdminPageHeader
        icon={BarChart3}
        title="Google Analytics"
        description="Send visits and sales from your live website to your Google Analytics account."
      />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Google Analytics 4</CardTitle>
            <Badge variant={integration.connected ? 'success' : 'outline'}>
              {integration.connected ? 'Connected' : 'Not connected'}
            </Badge>
          </div>
          <CardDescription>
            Enter the Measurement ID from your Google Analytics web data stream. It starts with G-.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <form.AppForm>
            <form.Form>
              <div className="space-y-5">
                <form.AppField name="measurement_id">
                  {(field) => (
                    <field.TextInput
                      label="Measurement ID"
                      placeholder="G-XXXXXXXXXX"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      transformValue={(value: string) => value.toUpperCase()}
                    />
                  )}
                </form.AppField>

                <div className="rounded-lg border border-border/60 bg-muted/25 p-4">
                  <Typography variant="label" className="block">
                    What your account will receive
                  </Typography>
                  <Typography variant="supporting" className="mt-1 block">
                    Page views and standard shopping activity from the live storefront, including
                    product views, cart changes, checkout steps, and purchases.
                  </Typography>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                  <a
                    href="https://support.google.com/analytics/answer/9539598"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
                  >
                    Find your Measurement ID
                    <ExternalLink className="size-3.5" />
                  </a>

                  {!isReadOnly ? (
                    <div className="ml-auto flex items-center gap-2">
                      {integration.connected ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              type="button"
                              variant="destructive"
                              disabled={removeMutation.isPending}
                            >
                              <Trash2 className="size-4" />
                              Disconnect
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Disconnect Google Analytics?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Your live website will stop sending new activity to this Google
                                Analytics account. Existing reports will not be deleted.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction asChild>
                                <Button variant="destructive" onClick={removeIntegration}>
                                  Disconnect
                                </Button>
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : null}
                      <SaveBar
                        mutation={updateMutation}
                        layout="inline"
                        renderInPortal={false}
                        submitButtonText={integration.connected ? 'Save' : 'Connect'}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </form.Form>
          </form.AppForm>
        </CardContent>
      </Card>
    </div>
  )
}

export function GoogleAnalyticsSettingsPage() {
  return (
    <IfGlobalOrg
      show={false}
      fallback={
        <div className="max-w-3xl space-y-5">
          <AdminPageHeader
            icon={BarChart3}
            title="Google Analytics"
            description="Send visits and sales from your live website to your Google Analytics account."
          />
          <OrganizationContextNotice>
            Switch to the florist account that owns the Google Analytics connection.
          </OrganizationContextNotice>
        </div>
      }
    >
      <OrganizationGoogleAnalyticsSettings />
    </IfGlobalOrg>
  )
}
